import { UseInfiniteQueryOptions, UseQueryOptions } from '@tanstack/react-query'

import { ApiPaginatedEntityResponse } from 'src/action-creators'
import { BuyerOrder, GetOrdersRequest } from 'src/types'

import { useEntityQuery, usePaginatedEntitiesQuery, usePatchEntityMutation } from '../entity'
import { PaginatedData, UseEntityMutationProps } from '../entity.types'

export const ORDERS_ENDPOINT = 'buyer/orders'
export const ORDERS_QUERY_KEY = 'orders'

export interface UseOrderQueryArgs<TData> {
  id?: Id
  queryOptions?: UseQueryOptions<BuyerOrder, Error, TData>
  queryParams?: GetOrdersRequest
}

export const useOrderQuery = <TData = BuyerOrder>({
  id,
  queryOptions,
  queryParams,
}: UseOrderQueryArgs<TData>) =>
  useEntityQuery<BuyerOrder, {}, TData>({
    endpoint: ORDERS_ENDPOINT,
    entityId: id,
    queryKey: ORDERS_QUERY_KEY,
    queryOptions,
    queryParams,
  })

export interface UsePaginatedOrdersQueryArgs<TData> {
  queryOptions?: UseInfiniteQueryOptions<
    ApiPaginatedEntityResponse<BuyerOrder>,
    Error,
    ApiPaginatedEntityResponse<TData>
  >
  queryParams?: GetOrdersRequest
}

export const usePaginatedOrdersQuery = <TData = BuyerOrder>({
  queryOptions = {},
  queryParams = {},
}: UsePaginatedOrdersQueryArgs<TData> = {}) =>
  usePaginatedEntitiesQuery<BuyerOrder, GetOrdersRequest, TData>({
    endpoint: ORDERS_ENDPOINT,
    queryKey: ORDERS_QUERY_KEY,
    queryParams,
    queryOptions,
  })

interface UsePatchOrderMutationArgs {
  queryOptions?: UseEntityMutationProps<
    Partial<BuyerOrder>,
    BuyerOrder,
    { previousData?: BuyerOrder | PaginatedData<BuyerOrder> }
  >['queryOptions']
}

export const usePatchOrderMutation = ({ queryOptions }: UsePatchOrderMutationArgs = {}) =>
  usePatchEntityMutation<
    Partial<BuyerOrder>,
    BuyerOrder,
    { previousData?: BuyerOrder | PaginatedData<BuyerOrder> }
  >({
    endpoint: ORDERS_ENDPOINT,
    queryOptions,
  })
